import React, { useState, useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Spin, Select, Checkbox, Tag, Collapse, message, Button, Dropdown, Modal, InputNumber, Switch, Space, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FileTextOutlined,
  LockOutlined,
  EditOutlined,
  CopyOutlined,
  CloseOutlined,
  HighlightOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import FileUpload from '../components/FileUpload';
import { fetchTestData } from '../services/api';
import AIPanel from '../components/AIPanel';
import ConclusionCard from '../components/ConclusionCard';
import ChartStyleEditor, { ChartStyleOverrides } from '../components/ChartStyleEditor';
import {
  getPreview, getColumns, recommendCharts, generateChart,
} from '../services/api';
import {
  CHART_ICONS, CHART_LABELS, CATEGORIES,
  DATAZOOM_SLIDER_TYPES, DATAZOOM_SCROLL_TYPES,
  applyStyleOverrides, injectDataZoom, injectToolbox, injectAxisRange, injectDarkMode,
  generateHtmlReport, downloadHtml,
  handleCopy4K as handleCopy4KUtil,
  chartActionBtnStyle as sharedChartActionBtnStyle,
} from '../utils/chartUtils';
import { useTheme } from '../contexts/ThemeContext';
import type { FileInfo, ColumnInfo, ChartConfig, AIExplanation, ConclusionData } from '../types';
import {
  AnnotationCanvas,
  AnnotationToolbar,
  annotationReducer,
  initialAnnotationStore,
} from '../annotations';

// CHART_ICONS, CHART_LABELS, CATEGORIES imported from chartUtils

const TYPE_TAG_COLORS: Record<string, string> = {
  float64: 'blue', int64: 'cyan', float: 'blue', integer: 'cyan',
  object: 'green', categorical: 'green', text: 'default',
  datetime: 'orange', 'datetime64[ns]': 'orange',
};

// applyStyleOverrides, injectDataZoom, generateHtmlReport, downloadHtml imported from chartUtils

// The following block (through URL.revokeObjectURL) was the old function body - now replaced
// by imports from chartUtils. We use sed-like approach to remove it.

const DEFAULT_STYLE: ChartStyleOverrides = {
  colorTheme: 'default', titleFontSize: 18, labelFontSize: 12, legendPosition: 'top',
};

interface ChartInfo {
  chart_type: string;
  display_name: string;
  icon: string;
  confidence: number;
  reason: string;
  compatible: boolean;
  require: string;
}

// ======================== Main Component ========================

const SmartChartWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Core state
  const [fileId, setFileId] = useState<string>(searchParams.get('fileId') || '');
  const [fileName, setFileName] = useState<string>(searchParams.get('fileName') || '');
  const [fileInfo, setFileInfo] = useState<{ rows: number; columns: number } | null>(null);

  // Data
  const [previewData, setPreviewData] = useState<Record<string, any>[]>([]);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [allColumns, setAllColumns] = useState<ColumnInfo[]>([]);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [xAxis, setXAxis] = useState<string>('');
  const [yAxis, setYAxis] = useState<string>('');
  const [groupBy, setGroupBy] = useState<string>('');

  // Chart
  const [currentChartType, setCurrentChartType] = useState<string>('');
  const [chartConfig, setChartConfig] = useState<ChartConfig | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [top3, setTop3] = useState<ChartInfo[]>([]);
  const [allCharts, setAllCharts] = useState<ChartInfo[]>([]);
  const [explanation, setExplanation] = useState<AIExplanation | null>(null);
  const [conclusion, setConclusion] = useState<ConclusionData | null>(null);

  // UI
  const [editingChart, setEditingChart] = useState<number>(-999); // -999 = none, -1 = single chart, 0/1/2... = multi-chart index
  // Per-chart style overrides: -1 = single chart, 0/1/2... = multi-chart indices
  const [styleMap, setStyleMap] = useState<Record<number, ChartStyleOverrides>>({
    [-1]: { ...DEFAULT_STYLE },
  });
  const getStyle = useCallback((index: number): ChartStyleOverrides => {
    return styleMap[index] || { ...DEFAULT_STYLE };
  }, [styleMap]);
  const setStyleForChart = useCallback((index: number, style: ChartStyleOverrides) => {
    setStyleMap((prev) => ({ ...prev, [index]: style }));
  }, []);
  const styleOverrides = getStyle(-1);
  const [dataLoading, setDataLoading] = useState(false);

  // Annotation system
  const [annotationStore, annotationDispatch] = useReducer(annotationReducer, initialAnnotationStore);
  const [annotationEnabled, setAnnotationEnabled] = useState(false);

  // Annotation persistence: load from localStorage on mount
  useEffect(() => {
    if (!fileId) return;
    try {
      const saved = localStorage.getItem(`trendata_ann_${fileId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        annotationDispatch({ type: 'LOAD_STORE', annotations: parsed });
      }
    } catch { /* ignore parse errors */ }
  }, [fileId]);

  // Annotation persistence: debounce save to localStorage
  useEffect(() => {
    if (!fileId) return;
    const timer = setTimeout(() => {
      const data = annotationStore.annotations;
      if (Object.keys(data).length > 0) {
        localStorage.setItem(`trendata_ann_${fileId}`, JSON.stringify(data));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [annotationStore.annotations, fileId]);
  // S5: AI timeout hint
  const [loadingHint, setLoadingHint] = useState('正在生成图表...');

  // Multi-chart mode
  const [multiMode, setMultiMode] = useState(false);
  const multiChartRefs = useRef<Record<number, any>>({});

  // Spec limits for capability_hist
  const [specModalOpen, setSpecModalOpen] = useState(false);
  const [pendingChartType, setPendingChartType] = useState<string>('');
  const [uslValue, setUslValue] = useState<number | null>(null);
  const [lslValue, setLslValue] = useState<number | null>(null);

  // Chart cache: key = `${chartType}_${selectedCols.join(',')}`
  const chartCacheRef = useRef<Map<string, { config: ChartConfig; explanation: AIExplanation | null; conclusion: ConclusionData | null }>>(new Map());
  const chartRef = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived data
  const numericCols = useMemo(
    () => allColumns.filter(c => ['float64', 'int64', 'float', 'integer'].includes(c.dtype)),
    [allColumns],
  );
  const categoricalCols = useMemo(
    () => allColumns.filter(c => ['object', 'categorical', 'text'].includes(c.dtype)),
    [allColumns],
  );

  // Categorize allCharts using MECE categories
  const categorizedCharts = useMemo(() => CATEGORIES.map(cat => ({
    ...cat,
    charts: allCharts.filter(c => c.compatible && cat.types.has(c.chart_type)),
  })), [allCharts]);
  const incompatibleCharts = useMemo(() => allCharts.filter(c => !c.compatible), [allCharts]);

  // ---- Load initial data ----
  const loadFileData = useCallback(async (fid: string) => {
    setDataLoading(true);
    try {
      const [preview, cols] = await Promise.all([getPreview(fid), getColumns(fid)]);
      setPreviewData(preview.data.slice(0, 10));
      setPreviewColumns(preview.columns);
      setFileInfo({ rows: preview.total_rows, columns: preview.columns.length });
      setAllColumns(cols);
      // Auto-select all columns
      const colNames = cols.map(c => c.name);
      setSelectedCols(colNames);
    } catch {
      message.error('加载数据失败');
    } finally {
      setDataLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (fileId) {
      loadFileData(fileId);
    }
  }, [fileId, loadFileData]);

  // ---- Fetch recommendations (debounced) ----
  const fetchRecommendations = useCallback(async (columns: string[]) => {
    if (!fileId || columns.length === 0) return;
    try {
      const res = await recommendCharts(fileId, columns);
      if (res.top3 && res.all_charts) {
        setTop3(res.top3);
        setAllCharts(res.all_charts);
      } else if (Array.isArray(res)) {
        setTop3(res);
        setAllCharts([]);
      }
    } catch {
      // Fallback
      setTop3([
        { chart_type: 'bar', display_name: '柱状图', icon: 'bar', confidence: 0.92, reason: '适合展示分类数据的数值对比', compatible: true, require: '' },
        { chart_type: 'line', display_name: '折线图', icon: 'line', confidence: 0.85, reason: '适合展示数据的变化趋势', compatible: true, require: '' },
        { chart_type: 'scatter', display_name: '散点图', icon: 'scatter', confidence: 0.78, reason: '适合探索变量之间的关系', compatible: true, require: '' },
      ]);
    }
  }, [fileId]);

  // Debounced column change => fetch recommendations + regenerate chart
  useEffect(() => {
    if (selectedCols.length === 0 || !fileId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchRecommendations(selectedCols);
      // If currently viewing a chart, regenerate it
      if (currentChartType) {
        handleSelectChart(currentChartType, true);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [selectedCols, fileId]); // eslint-disable-line

  // ---- Generate chart ----
  const handleSelectChart = useCallback(async (chartType: string, forceRefresh = false) => {
    if (chartType === 'capability_hist' && !forceRefresh) {
      setPendingChartType('capability_hist');
      setSpecModalOpen(true);
      return;
    }

    setCurrentChartType(chartType);
    const cacheKey = `${chartType}_${selectedCols.join(',')}_x:${xAxis}_y:${yAxis}_g:${groupBy}`;

    // Use cache if available and not forced
    if (!forceRefresh && chartCacheRef.current.has(cacheKey)) {
      const cached = chartCacheRef.current.get(cacheKey)!;
      setChartConfig(cached.config);
      setExplanation(cached.explanation);
      setConclusion(cached.conclusion);
      return;
    }

    setChartLoading(true);
    setLoadingHint('正在生成图表...');
    const hintTimer = setTimeout(() => setLoadingHint('AI 分析中，请耐心等待...'), 5000);
    try {
      const options: Record<string, any> = {};
      if (xAxis) options.x_axis = xAxis;
      if (yAxis) options.y_axis = yAxis;
      if (groupBy) options.group_by = groupBy;
      if (chartType === 'capability_hist') {
        if (uslValue !== null) options.usl = uslValue;
        if (lslValue !== null) options.lsl = lslValue;
      }

      const config = await generateChart(fileId, chartType, selectedCols, options);
      setChartConfig(config);

      const expl = config.ai_explanation || null;
      setExplanation(expl);

      // Try to extract conclusion from config
      const concl = (config as any).conclusion || null;
      setConclusion(concl);

      // Cache it
      chartCacheRef.current.set(cacheKey, { config, explanation: expl, conclusion: concl });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        message.error('数据已过期，请重新上传文件');
        setFileId('');
      } else {
        message.error('图表生成失败，请重试');
      }
    } finally {
      clearTimeout(hintTimer);
      setChartLoading(false);
    }
  }, [fileId, selectedCols, xAxis, yAxis, groupBy, uslValue, lslValue]);

  // ---- Spec confirm ----
  const handleSpecConfirm = useCallback(async () => {
    setSpecModalOpen(false);
    // Read USL/LSL directly from DOM to avoid stale closure
    setCurrentChartType('capability_hist');
    setChartLoading(true);
    try {
      const options: Record<string, any> = {};
      if (uslValue !== null) options.usl = uslValue;
      if (lslValue !== null) options.lsl = lslValue;
      if (xAxis) options.x_axis = xAxis;
      if (yAxis) options.y_axis = yAxis;
      if (groupBy) options.group_by = groupBy;

      const config = await generateChart(fileId, 'capability_hist', selectedCols, options);
      setChartConfig(config);
      setExplanation(config.ai_explanation || null);
      setConclusion((config as any).conclusion || null);
      chartCacheRef.current.set(`capability_hist_${selectedCols.join(',')}`, {
        config, explanation: config.ai_explanation || null, conclusion: (config as any).conclusion || null,
      });
    } catch {
      message.error('能力直方图生成失败，请重试');
    } finally {
      setChartLoading(false);
    }
  }, [fileId, selectedCols, xAxis, yAxis, groupBy, uslValue, lslValue]);

  // ---- Column toggle ----
  const toggleColumn = useCallback((colName: string) => {
    setSelectedCols(prev =>
      prev.includes(colName) ? prev.filter(c => c !== colName) : [...prev, colName],
    );
  }, []);

  const toggleAllColumns = useCallback(() => {
    const allNames = allColumns.map(c => c.name);
    setSelectedCols(prev => prev.length === allNames.length ? [] : allNames);
  }, [allColumns]);

  // ---- Export ----
  const handleExportPng = useCallback(() => {
    if (!chartRef.current) return;
    const instance = chartRef.current.getEchartsInstance();
    const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    const a = document.createElement('a');
    a.href = url;
    a.download = `trendata_${currentChartType}_${Date.now()}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    message.success('PNG 已导出');
  }, [currentChartType]);

  // S9: SVG export
  const handleExportSvg = useCallback(() => {
    if (!chartRef.current) return;
    const instance = chartRef.current.getEchartsInstance();
    const url = instance.getDataURL({ type: 'svg' });
    const a = document.createElement('a');
    a.href = url;
    a.download = `trendata_${currentChartType}_${Date.now()}.svg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    message.success('SVG 已导出');
  }, [currentChartType]);

  const handleExportHtml = useCallback(() => {
    if (!chartConfig || !explanation) return;
    const finalOption = applyStyleOverrides(chartConfig.option, styleOverrides, currentChartType);
    const html = generateHtmlReport(
      chartConfig.title || CHART_LABELS[currentChartType] || '图表',
      finalOption, explanation, fileId,
    );
    downloadHtml(html, currentChartType);
    message.success('HTML 报告已导出');
  }, [chartConfig, explanation, styleOverrides, currentChartType, fileId]);

  // ---- Computed final chart option ----
  const finalOption = useMemo(() => {
    if (!chartConfig?.option) return null;
    let opt = applyStyleOverrides(chartConfig.option, styleOverrides, currentChartType);
    opt = injectDataZoom(opt, currentChartType);
    opt = injectToolbox(opt);
    opt = injectAxisRange(opt);
    opt = injectDarkMode(opt, isDark);
    return opt;
  }, [chartConfig, styleOverrides, currentChartType, isDark]);

  // ---- Multi-chart configs (split series) ----
  const multiCharts = useMemo(() => {
    if (!chartConfig || !multiMode) return [];
    const option = chartConfig.option;
    const charts: { title: string; option: Record<string, any> }[] = [];
    if (option.series && Array.isArray(option.series) && option.series.length > 1) {
      option.series.forEach((s: any, idx: number) => {
        const singleOption: Record<string, any> = { ...option, series: [s], legend: undefined };
        charts.push({ title: s.name || `图表 ${idx + 1}`, option: singleOption });
      });
    }
    if (charts.length === 0) {
      charts.push({ title: chartConfig.title || '图表', option: chartConfig.option });
    }
    return charts;
  }, [chartConfig, multiMode]);

  // ---- Multi-chart export ----
  const handleExportMultiPng = useCallback((idx: number) => {
    const ref = multiChartRefs.current[idx];
    if (!ref) return;
    const instance = ref.getEchartsInstance();
    const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    const a = document.createElement('a');
    a.href = url;
    a.download = `trendata_${currentChartType}_sub${idx + 1}_${Date.now()}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    message.success('PNG 已导出');
  }, [currentChartType]);

  // ---- Copy 4K image to clipboard (delegates to shared util) ----
  const handleCopy4K = useCallback(async (chartIndex: number) => {
    let echartsInstance: any = null;
    if (chartIndex === -1) {
      echartsInstance = chartRef.current?.getEchartsInstance();
    } else {
      echartsInstance = multiChartRefs.current[chartIndex]?.getEchartsInstance();
    }
    handleCopy4KUtil(echartsInstance, message);
  }, []);

  // Chart action button style (from shared utils)
  const chartActionBtnStyle = sharedChartActionBtnStyle;

  // ---- Upload state (no fileId) ----
  if (!fileId) {
    return (
      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 300px', gap: 32, alignItems: 'start',
        }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase',
              color: '#533afd', marginBottom: 12,
            }}>
              Step 1 / 3 · 上传数据
            </div>
            <h2 style={{
              fontSize: 22, fontWeight: 700, color: 'var(--title-color)',
              margin: '0 0 8px', lineHeight: 1.3,
            }}>
              上传数据文件
            </h2>
            <p style={{
              fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px', lineHeight: 1.6,
            }}>
              上传后 AI 自动推荐最佳图表类型，42 种交互式可视化可选。
            </p>
            <FileUpload
              onUploadSuccess={(info: FileInfo) => {
                setFileId(info.file_id);
                setFileName(info.filename);
                setFileInfo({ rows: info.rows, columns: info.columns });
                setSearchParams({ fileId: info.file_id, fileName: info.filename }, { replace: true });
              }}
            />
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>或使用示例数据：</span>
              {[
                { key: 'sample_sales', label: '销售时序', color: '#15be53' },
                { key: 'sample_manufacturing', label: '制造业质量', color: '#533afd' },
              ].map(s => (
                <button key={s.key} onClick={async () => {
                  try {
                    const td = await fetchTestData(s.key);
                    setFileId(td.file_id);
                    setFileName(td.filename);
                    setFileInfo({ rows: td.rows, columns: td.columns });
                    setSearchParams({ fileId: td.file_id, fileName: td.filename }, { replace: true });
                  } catch (e: any) {
                    console.error(e);
                  }
                }}
                  style={{
                    padding: '4px 12px', fontSize: 12, fontWeight: 500,
                    background: `${s.color}12`, color: s.color,
                    border: `1px solid ${s.color}33`, borderRadius: 14,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >{s.label}</button>
              ))}
            </div>
          </div>
          <div style={{
            background: isDark ? '#0f1629' : '#061b31',
            borderRadius: 8, padding: '24px 20px',
            color: '#fff',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>图表流程</div>
            {[
              { n: '1', label: '上传数据', desc: 'CSV / Excel 文件', active: true },
              { n: '2', label: '选择图表', desc: 'AI 推荐 + 42 种类型', active: false },
              { n: '3', label: '导出分享', desc: '4K PNG / SVG / HTML', active: false },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                marginBottom: i < 2 ? 16 : 0,
                opacity: s.active ? 1 : 0.4,
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                  background: s.active ? '#533afd' : 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                }}>{s.n}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{s.desc}</div>
                </div>
              </div>
            ))}
            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              fontSize: 11, color: 'rgba(255,255,255,0.4)',
            }}>
              42 种图表 · AI 推荐 · 4K 导出
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Loading state ----
  if (dataLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
        <Spin size="large" tip="正在加载数据..." />
      </div>
    );
  }

  // ---- Chart grid card component ----
  const ChartGridCard = ({ chart }: { chart: ChartInfo }) => (
    <div
      onClick={() => chart.compatible ? handleSelectChart(chart.chart_type) : message.warning(`${chart.display_name}不适用于当前数据：${chart.require}`)}
      style={{
        padding: '10px 12px', borderRadius: 10, cursor: chart.compatible ? 'pointer' : 'not-allowed',
        border: `2px solid ${currentChartType === chart.chart_type ? '#533afd' : '#e8e8e8'}`,
        background: currentChartType === chart.chart_type ? '#f0f5ff' : chart.compatible ? '#fff' : '#fafafa',
        opacity: chart.compatible ? 1 : 0.5, transition: 'all 0.2s',
        display: 'flex', alignItems: 'center', gap: 8,
      }}
    >
      <span style={{ fontSize: 18, color: chart.compatible ? '#533afd' : '#bbb' }}>
        {CHART_ICONS[chart.chart_type] || <BarChartOutlined />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {chart.display_name}
        </div>
        {chart.confidence > 0 && (
          <div style={{ fontSize: 10, color: '#999' }}>{Math.round(chart.confidence * 100)}%</div>
        )}
      </div>
      {!chart.compatible && <LockOutlined style={{ fontSize: 12, color: '#bbb' }} />}
    </div>
  );

  return (
    <div style={{ background: '#f5f6fa', minHeight: 'calc(100vh - 130px)', margin: '-16px -24px', padding: 0 }}>
      {/* ---- Top toolbar ---- */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '10px 24px',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/')}>
          返回
        </Button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>{fileName}</span>
          {fileInfo && (
            <Tag color="default" style={{ fontSize: 11 }}>
              {fileInfo.rows} 行 x {fileInfo.columns} 列
            </Tag>
          )}
        </div>
        <span style={{ fontSize: 13, color: '#666' }}>多图模式</span>
        <Switch checked={multiMode} onChange={setMultiMode} size="small" />
        <Dropdown
          menu={{
            items: [
              { key: 'png', icon: <FileImageOutlined />, label: '导出 PNG', disabled: !chartConfig, onClick: handleExportPng },
              { key: 'svg', icon: <FileImageOutlined />, label: '导出 SVG', disabled: !chartConfig, onClick: handleExportSvg },
              { key: 'html', icon: <FileTextOutlined />, label: '导出 HTML 报告', disabled: !chartConfig || !explanation, onClick: handleExportHtml },
            ],
          }}
        >
          <Button icon={<DownloadOutlined />}>导出</Button>
        </Dropdown>
      </div>

      {/* ---- Main three-column layout ---- */}
      <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 196px)' }}>
        {/* ---- Left panel - Data ---- */}
        <div style={{
          width: 260, minWidth: 260, background: '#fff', borderRight: '1px solid #e8e8e8',
          overflowY: 'auto', padding: 16, paddingBottom: 20, display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          {/* Mini data table */}
          <div>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              数据预览
            </div>
            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
              <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {previewColumns.slice(0, 5).map(col => (
                      <th key={col} style={{
                        padding: '4px 6px', background: '#fafafa', borderBottom: '1px solid #f0f0f0',
                        fontWeight: 600, whiteSpace: 'nowrap', maxWidth: 80, overflow: 'hidden',
                        textOverflow: 'ellipsis', fontSize: 10, color: '#666', position: 'sticky', top: 0,
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    <tr key={i}>
                      {previewColumns.slice(0, 5).map(col => (
                        <td key={col} style={{
                          padding: '3px 6px', borderBottom: '1px solid #f8f8f8',
                          maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          fontSize: 11, color: '#444',
                        }}>
                          {row[col] != null ? String(row[col]).slice(0, 12) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewColumns.length > 5 && (
              <div style={{ fontSize: 10, color: '#bbb', marginTop: 4 }}>
                +{previewColumns.length - 5} 列未显示
              </div>
            )}
          </div>

          {/* Column selection */}
          <div>
            <div style={{
              fontSize: 11, color: '#999', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>列选择</span>
              <span style={{ fontSize: 10, color: '#533afd', cursor: 'pointer' }} onClick={toggleAllColumns}>
                {selectedCols.length === allColumns.length ? '取消全选' : '全选'}
              </span>
            </div>
            <div style={{ maxHeight: 250, overflow: 'auto' }}>
              {allColumns.map(col => (
                <div
                  key={col.name}
                  onClick={() => toggleColumn(col.name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 8px', borderRadius: 6, marginBottom: 2,
                    background: selectedCols.includes(col.name) ? '#f0f5ff' : 'transparent',
                    cursor: 'pointer', fontSize: 12, transition: 'background 0.15s',
                  }}
                >
                  <Checkbox checked={selectedCols.includes(col.name)} style={{ pointerEvents: 'none' }} />
                  <span style={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {col.name}
                  </span>
                  <Tag
                    color={TYPE_TAG_COLORS[col.dtype] || 'default'}
                    style={{ fontSize: 10, lineHeight: '16px', margin: 0, padding: '0 4px' }}
                  >
                    {col.dtype}
                  </Tag>
                </div>
              ))}
            </div>
          </div>

          {/* Axis variables */}
          <div>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              轴变量
            </div>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>X 轴</div>
            <Select
              size="small" value={xAxis || undefined} onChange={(v) => setXAxis(v || '')}
              style={{ width: '100%', marginBottom: 8 }} allowClear placeholder="自动"
              options={selectedCols.map(c => ({ value: c, label: c }))}
            />
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Y 轴</div>
            <Select
              size="small" value={yAxis || undefined} onChange={(v) => setYAxis(v || '')}
              style={{ width: '100%', marginBottom: 8 }} allowClear placeholder="自动"
              options={numericCols.filter(c => selectedCols.includes(c.name)).map(c => ({ value: c.name, label: c.name }))}
            />
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>分组</div>
            <Select
              size="small" value={groupBy || undefined} onChange={(v) => setGroupBy(v || '')}
              style={{ width: '100%' }} allowClear placeholder="不分组"
              options={categoricalCols.filter(c => selectedCols.includes(c.name)).map(c => ({ value: c.name, label: c.name }))}
            />
          </div>

          {/* Stats summary */}
          <div style={{ fontSize: 11, color: '#999' }}>
            <div style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              数据概况
            </div>
            <div>已选 {selectedCols.length} / {allColumns.length} 列</div>
            <div>数值列: {numericCols.filter(c => selectedCols.includes(c.name)).length}</div>
            <div>分类列: {categoricalCols.filter(c => selectedCols.includes(c.name)).length}</div>
          </div>
        </div>

        {/* ---- Center main area - Chart + Conclusion ---- */}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: 20 }}>
          {/* AI Top 3 recommendations - compact horizontal cards */}
          {top3.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {top3.map(rec => (
                <div
                  key={rec.chart_type}
                  onClick={() => handleSelectChart(rec.chart_type)}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    background: currentChartType === rec.chart_type ? '#f0f5ff' : '#fff',
                    border: `1.5px solid ${currentChartType === rec.chart_type ? '#533afd' : '#e8e8e8'}`,
                    display: 'flex', alignItems: 'center', gap: 8,
                    transition: 'all 0.2s',
                  }}
                >
                  <span style={{ fontSize: 18, color: '#533afd' }}>
                    {CHART_ICONS[rec.chart_type] || <BarChartOutlined />}
                  </span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#061b31' }}>{rec.display_name}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{Math.round(rec.confidence * 100)}%</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Chart preview area */}
          {!multiMode ? (
            <div style={{
              position: 'relative',
              background: '#fff', borderRadius: 12, padding: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 16, minHeight: 400,
            }}>
              {chartLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Spin size="large" tip={loadingHint} />
                </div>
              ) : finalOption ? (
                <>
                  <div style={{ overflow: 'hidden', borderRadius: 12, position: 'relative' }}>
                    <ReactEChartsCore
                      ref={chartRef}
                      option={finalOption}
                      style={{ height: Math.min((finalOption as any)._chart_height || 380, 600), width: '100%' }}
                      notMerge={true}
                    />
                    <AnnotationCanvas
                      chartRef={chartRef}
                      chartId="smart-chart-main"
                      store={annotationStore}
                      dispatch={annotationDispatch}
                      enabled={annotationEnabled}
                    />
                  </div>
                  {/* Action buttons */}
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, zIndex: 10 }}>
                    <Tooltip title={annotationEnabled ? '关闭标注' : '标注'}>
                      <Button size="small" shape="circle" icon={<HighlightOutlined />}
                        onClick={() => setAnnotationEnabled(prev => !prev)}
                        style={{
                          ...chartActionBtnStyle,
                          ...(annotationEnabled ? { background: '#533afd', color: '#fff', borderColor: '#533afd' } : {}),
                        }} />
                    </Tooltip>
                    <Tooltip title="编辑样式">
                      <Button size="small" shape="circle" icon={<EditOutlined />}
                        onClick={() => setEditingChart(-1)}
                        style={chartActionBtnStyle} />
                    </Tooltip>
                    <Tooltip title="复制 4K 图片">
                      <Button size="small" shape="circle" icon={<CopyOutlined />}
                        onClick={() => handleCopy4K(-1)}
                        style={chartActionBtnStyle} />
                    </Tooltip>
                  </div>
                  {/* Annotation toolbar */}
                  {annotationEnabled && (
                    <AnnotationToolbar
                      store={annotationStore}
                      dispatch={annotationDispatch}
                      chartId="smart-chart-main"
                      chartRef={chartRef}
                    />
                  )}
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: 400, color: '#bbb',
                }}>
                  <BarChartOutlined style={{ fontSize: 48, marginBottom: 16, color: '#ddd' }} />
                  <div style={{ fontSize: 16, color: '#bbb' }}>点击上方推荐图表，或展开下方图表网格选择</div>
                  <div style={{ fontSize: 12, color: '#ddd', marginTop: 8 }}>
                    支持 {Object.keys(CHART_LABELS).length} 种图表类型
                  </div>
                </div>
              )}
              {/* K7: Editing overlay - always rendered, transform-controlled */}
              <div style={{
                position: 'absolute', top: 0, right: 0, bottom: 0,
                width: 320, zIndex: 20,
                transform: editingChart === -1 ? 'translateX(0)' : 'translateX(100%)',
                opacity: editingChart === -1 ? 1 : 0,
                pointerEvents: editingChart === -1 ? 'auto' : 'none',
                transition: 'transform 0.3s ease, opacity 0.25s ease',
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(12px)',
                borderLeft: '1px solid rgba(102,126,234,0.15)',
                borderRadius: '0 12px 12px 0',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
                padding: '12px 14px',
                overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>编辑样式</span>
                  <Button shape="circle" size="small" icon={<CloseOutlined />}
                    onClick={() => setEditingChart(-999)}
                    style={{ border: 'none', background: 'rgba(0,0,0,0.04)' }} />
                </div>
                <ChartStyleEditor
                  style={getStyle(-1)}
                  onChange={(s) => setStyleForChart(-1, s)}
                />
              </div>
            </div>
          ) : (
            /* Multi-chart grid mode */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
              {chartLoading ? (
                <div style={{ padding: 24, gridColumn: '1 / -1', textAlign: 'center', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <Spin size="large" tip={loadingHint} />
                </div>
              ) : multiCharts.length > 0 ? (
                multiCharts.map((mc, idx) => {
                  const chartStyle = getStyle(idx);
                  let subOpt = applyStyleOverrides(mc.option, chartStyle, currentChartType);
                  subOpt = injectDataZoom(subOpt, currentChartType);
                  subOpt = injectToolbox(subOpt);
                  subOpt = injectAxisRange(subOpt);
                  subOpt = injectDarkMode(subOpt, isDark);
                  return (
                    <div key={idx} style={{ position: 'relative', background: '#fff', borderRadius: 10, padding: 12, border: '1px solid #e8e8e8' }}>
                      <h4 style={{ fontSize: 13, fontWeight: 600, color: '#061b31', marginBottom: 8, margin: '0 0 8px 0' }}>
                        {chartStyle.title || mc.title || `图表 ${idx + 1}`}
                      </h4>
                      <div style={{ overflow: 'hidden', borderRadius: 8 }}>
                        <ReactEChartsCore
                          ref={(el: any) => { if (el) multiChartRefs.current[idx] = el; }}
                          option={subOpt}
                          style={{ height: 280, width: '100%' }}
                          notMerge={true}
                        />
                      </div>
                      {/* Action buttons */}
                      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, zIndex: 10 }}>
                        <Tooltip title="编辑样式">
                          <Button size="small" shape="circle" icon={<EditOutlined />}
                            onClick={() => setEditingChart(idx)}
                            style={chartActionBtnStyle} />
                        </Tooltip>
                        <Tooltip title="复制 4K 图片">
                          <Button size="small" shape="circle" icon={<CopyOutlined />}
                            onClick={() => handleCopy4K(idx)}
                            style={chartActionBtnStyle} />
                        </Tooltip>
                      </div>
                      {/* K7: Editing overlay - always rendered, transform-controlled */}
                      <div style={{
                        position: 'absolute', top: 0, right: 0, bottom: 0,
                        width: 'min(320px, 100%)', zIndex: 20,
                        transform: editingChart === idx ? 'translateX(0)' : 'translateX(100%)',
                        opacity: editingChart === idx ? 1 : 0,
                        pointerEvents: editingChart === idx ? 'auto' : 'none',
                        transition: 'transform 0.3s ease, opacity 0.25s ease',
                        background: 'rgba(255,255,255,0.88)',
                        backdropFilter: 'blur(12px)',
                        borderLeft: '1px solid rgba(102,126,234,0.15)',
                        borderRadius: '0 12px 12px 0',
                        boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
                        padding: '12px 14px',
                        overflowY: 'auto',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>编辑样式</span>
                          <Button shape="circle" size="small" icon={<CloseOutlined />}
                            onClick={() => setEditingChart(-999)}
                            style={{ border: 'none', background: 'rgba(0,0,0,0.04)' }} />
                        </div>
                        <ChartStyleEditor
                          style={chartStyle}
                          onChange={(s) => setStyleForChart(idx, s)}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: 24, gridColumn: '1 / -1', textAlign: 'center', background: '#fff', borderRadius: 12, color: '#bbb' }}>
                  <BarChartOutlined style={{ fontSize: 48, marginBottom: 16, color: '#ddd' }} />
                  <div>请先选择一个图表类型</div>
                </div>
              )}
            </div>
          )}

          {/* Conclusion + AI explanation */}
          {(explanation || conclusion) && (
            <div style={{ display: 'grid', gridTemplateColumns: conclusion ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 16 }}>
              {conclusion && (
                <ConclusionCard
                  status={conclusion.status}
                  title={conclusion.title}
                  message={conclusion.message}
                  details={conclusion.details}
                />
              )}
              {explanation && <AIPanel explanation={explanation} />}
            </div>
          )}

          {/* Export buttons */}
          {chartConfig && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Dropdown
                menu={{
                  items: [
                    { key: 'png', icon: <FileImageOutlined />, label: '导出 PNG', onClick: handleExportPng },
                    { key: 'svg', icon: <FileImageOutlined />, label: '导出 SVG', onClick: handleExportSvg },
                    { key: 'html', icon: <FileTextOutlined />, label: '导出 HTML 报告', disabled: !explanation, onClick: handleExportHtml },
                  ],
                }}
              >
                <Button icon={<DownloadOutlined />} size="small">导出</Button>
              </Dropdown>
            </div>
          )}
        </div>

        {/* ---- Right panel - Chart type selection ---- */}
        <div style={{
          width: 280, minWidth: 280, background: '#fff', borderLeft: '1px solid #e8e8e8',
          overflowY: 'auto', padding: 16,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#061b31', marginBottom: 12, marginTop: 0 }}>选择图表</h3>

          {categorizedCharts.map(cat => (
            cat.charts.length > 0 && (
              <div key={cat.key} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: cat.color,
                  borderLeft: `3px solid ${cat.color}`, paddingLeft: 8,
                  marginBottom: 8,
                }}>
                  {cat.label} ({cat.charts.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {cat.charts.map(chart => (
                    <div
                      key={chart.chart_type}
                      onClick={() => handleSelectChart(chart.chart_type)}
                      style={{
                        padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                        fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
                        background: currentChartType === chart.chart_type ? '#f0f5ff' : '#fafafa',
                        border: `1px solid ${currentChartType === chart.chart_type ? cat.color : '#e8e8e8'}`,
                        color: currentChartType === chart.chart_type ? cat.color : '#666',
                        transition: 'all 0.2s',
                      }}
                    >
                      <span style={{ fontSize: 14 }}>{CHART_ICONS[chart.chart_type] || <BarChartOutlined />}</span>
                      <span>{chart.display_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {/* Incompatible types - collapsed */}
          {incompatibleCharts.length > 0 && (
            <Collapse ghost size="small">
              <Collapse.Panel header={<span style={{ fontSize: 12, color: '#999' }}>不兼容 ({incompatibleCharts.length})</span>} key="disabled">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {incompatibleCharts.map(chart => (
                    <div
                      key={chart.chart_type}
                      onClick={() => message.warning(`${chart.display_name}不适用于当前数据：${chart.require}`)}
                      style={{
                        padding: '4px 8px', borderRadius: 6, cursor: 'not-allowed',
                        fontSize: 10, display: 'flex', alignItems: 'center', gap: 4,
                        background: '#fafafa', border: '1px solid #f0f0f0',
                        color: '#bbb', opacity: 0.7,
                      }}
                    >
                      <span style={{ fontSize: 12 }}>{CHART_ICONS[chart.chart_type] || <BarChartOutlined />}</span>
                      <span>{chart.display_name}</span>
                      <LockOutlined style={{ fontSize: 10 }} />
                    </div>
                  ))}
                </div>
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
      </div>

      {/* ---- Spec Limits Modal ---- */}
      <Modal
        title="设置规格限 (能力直方图)"
        open={specModalOpen}
        onOk={handleSpecConfirm}
        onCancel={() => setSpecModalOpen(false)}
        okText="生成图表"
        cancelText="取消"
      >
        <div style={{ display: 'flex', gap: 16, padding: '16px 0' }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 8, fontSize: 13 }}>规格下限 (LSL)</div>
            <InputNumber
              style={{ width: '100%' }}
              value={lslValue}
              onChange={(v) => setLslValue(v)}
              placeholder="可选"
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 8, fontSize: 13 }}>规格上限 (USL)</div>
            <InputNumber
              style={{ width: '100%' }}
              value={uslValue}
              onChange={(v) => setUslValue(v)}
              placeholder="可选"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SmartChartWorkspace;
